import { useEffect, useState } from "react";
import { type AcademicSnapshot, type FallbackTier, CHONBURI } from "@mtt/shared";
import { useTheme } from "../hooks/useTheme";
import { formatDate } from "../lib/time";

interface FeedHealth {
  label: string;
  tier: FallbackTier | "loading";
  ageMinutes: number;
}

interface Props {
  feeds: FeedHealth[];
  onOpenCatalog: () => void;
  catalogCount: number;
  viewMode: "2D" | "3D" | "3DS";
  onCycleViewMode: () => void;
  onOpenManual: () => void;
  onOpenWhitepaper: () => void;
  onOpenSheets: () => void;
  onOpenAtlas: () => void;
  onOpenPlatform: () => void;
  onFlip?: () => void;
  sheetsConfigured: boolean;
  academic: AcademicSnapshot | null;
  systemStatus?: "healthy" | "degraded" | "down" | "unknown";
}


const TEMPO_COLOR: Record<AcademicSnapshot["tempo"], string> = {
  low: "var(--text-3)",
  normal: "var(--data)",
  high: "var(--warn)",
  peak: "var(--bad)",
};

export function TopBar({ feeds, onOpenCatalog, catalogCount, viewMode, onCycleViewMode, onOpenManual, onOpenWhitepaper, onOpenSheets, onOpenAtlas, onOpenPlatform, onFlip, sheetsConfigured, academic, systemStatus }: Props) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const { theme, toggle } = useTheme();
  const liveCount = feeds.filter((f) => f.tier === "live").length;

  return (
    <header className="topbar">
      {/* Brand + identity bar */}
      <div className="brand">
        {/* IMPACT — principal mark (operator: Bangkok Land / IMPACT). Theme-swapped:
            dark-variant wordmark on the void topbar, original on light theme. */}
        <a className="brand-impact" href="https://www.impact.co.th" target="_blank"
           rel="noreferrer" aria-label="IMPACT Muang Thong Thani">
          <img className="logo-dark" src="/logos/impact-dark.svg" alt="IMPACT Muang Thong Thani" />
          <img className="logo-light" src="/logos/impact.svg" alt="IMPACT Muang Thong Thani" />
        </a>
        {/* Government co-brand / partners */}
        <a className="sponsor" href="https://www.depa.or.th" target="_blank"
           rel="noreferrer" aria-label="depa — Digital Economy Promotion Agency">
          <img src="/logos/depa.jpg" alt="depa" />
        </a>
        <a className="sponsor" href="https://www.smartcitythailand.or.th" target="_blank"
           rel="noreferrer" aria-label="Smart City Thailand">
          <img src="/logos/smart-city-thailand.jpg" alt="Smart City Thailand" />
        </a>
        <a className="sponsor sponsor-axiom" href="https://axiom.nonarkara.org" target="_blank"
           rel="noreferrer" aria-label="Axiom — Innovation as a Service">
          <img src="/logos/axiom.png" alt="Axiom" />
        </a>
        <div className="brand-stack">
          <strong>{CHONBURI.name.en}</strong>
          <span className="brand-sub mono">
            MTT-01 · Pak Kret, Nonthaburi
          </span>
        </div>
      </div>

      {/* Live feed chips */}
      <div className="feeds">
        <span className="feeds-label">FEEDS</span>
        {feeds.map((f) => (
          <span
            key={f.label}
            className={`feed-chip feed-${f.tier}`}
            title={`${f.tier} · ${f.ageMinutes}m ago`}
          >
            <span className={`dot ${f.tier === "loading" ? "loading" : f.tier}`} />
            {f.label}
          </span>
        ))}
      </div>

      <div className="topbar-right">
        <span
          className="live-count mono"
          role="status"
          aria-label={`${liveCount} of ${feeds.length} data feeds live`}
        >
          <span className="dot live" style={{ marginRight: 5 }} />
          {liveCount}/{feeds.length} LIVE
        </span>
        {systemStatus && systemStatus !== "healthy" && (
          <span
            className={`system-status-badge status-${systemStatus}`}
            title={`System ${systemStatus.toUpperCase()} — check /api/health/detailed`}
          >
            <span className={`dot ${systemStatus}`} />
            {systemStatus.toUpperCase()}
          </span>
        )}
        <button
          onClick={onOpenSheets}
          className={`mono sheets-btn ${sheetsConfigured ? "sheets-btn-live" : ""}`}
          aria-label={sheetsConfigured ? "Open Google Sheets live feed" : "Set up Google Sheets integration"}
          title={sheetsConfigured ? "Open the live-data Google Sheet" : "Connect a Google Sheet to this dashboard"}
        >
          {sheetsConfigured ? "▦ SHEETS" : "▦ SHEETS"}
        </button>
        <button onClick={onOpenCatalog} className="mono" aria-label="Open source catalog">
          SOURCES · {catalogCount}
        </button>
        <button
          onClick={onOpenAtlas}
          className="atlas-toggle"
          aria-label="Open the Yala Data Atlas — outcome indicators, charts, and the full data-source catalog"
          title="Data Atlas — poverty, education, health, climate, economy outcomes + 200+ sources"
        >
          ◷ ATLAS
        </button>
        <button
          onClick={onOpenPlatform}
          className="kp-toggle"
          aria-label="Open the Yala Knowledge Platform — search, academy, AI concierge, archive"
          title="Knowledge Platform — search · learn · ask AI · time-machine archive"
        >
          ⌕ LEARN
        </button>
        {onFlip ? (
          <button
            onClick={onFlip}
            className="flip-toggle"
            aria-label="Flip to the Watch Terminal — Bloomberg-style real-time + reference data"
            title="Flip to the Watch Terminal (System B)"
          >
            ⇄ TERMINAL
          </button>
        ) : null}
        <button
          onClick={onOpenWhitepaper}
          className="mono"
          aria-label="Open whitepaper — platform overview and usage manual (TH/EN)"
          title="Whitepaper — platform overview · Thai + English"
        >
          WP
        </button>
        <button
          onClick={onOpenManual}
          className="mono manual-trigger"
          aria-label="Open user manual"
          title="Manual — every button, color, acronym"
        >
          ?
        </button>
        <button
          onClick={onCycleViewMode}
          className={`mono dim-toggle vm-${viewMode}`}
          aria-label={`View mode: ${viewMode}. Click to cycle 2D → 3D → 3D substructure`}
          title={
            viewMode === "2D"
              ? "Tap → 3D · extrude buildings"
              : viewMode === "3D"
                ? "Tap → 3DS · transparent superstructure, show underground"
                : "Tap → 2D · top-down"
          }
        >
          {viewMode}
        </button>
        <button
          onClick={toggle}
          className="theme-toggle"
          aria-label={`Switch to ${theme === "dark" ? "light" : "dark"} theme`}
          title={`${theme === "dark" ? "Light" : "Dark"} theme`}
        >
          {theme === "dark" ? "☀" : "☾"}
        </button>
      </div>

      <div className="clock">
        {academic?.current && (
          <span
            className="mono academic-chip"
            style={{ color: TEMPO_COLOR[academic.tempo] }}
            title={`${academic.current.label} · ${academic.current.describe}${
              academic.next && academic.daysToNext != null
                ? ` · next: ${academic.next.label} in ${academic.daysToNext}d`
                : ""
            }`}
          >
            {academic.current.label.toUpperCase()}
          </span>
        )}
        <span className="clock-date">{formatDate(now)}</span>
        <span className="clock-time">
          {now.toLocaleTimeString("en-GB", { hour12: false })}
        </span>
      </div>
    </header>
  );
}
